import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Car, Calendar, Bell, User, Settings, LogOut, Search, 
  CheckCircle, AlertCircle, Clock, CreditCard, Plus, MessageCircle, Headphones, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { getVehiclePrimaryImage, handleVehicleImageError } from '../lib/imageUtils';
import InviteAndEarn from '../components/InviteAndEarn';
import { CreditBalanceCard } from '../components/CreditApplySection';
import ReviewModal from '../components/ReviewModal';
import PaymentCountdown from '../components/PaymentCountdown';

const CustomerDashboard = () => {
  const { user, logout, api, isOwner, isPlatformOwner, isSuperAdmin, isAdmin, refreshUser, updateUser, isHost } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentReviewBooking, setCurrentReviewBooking] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, notificationsRes, verificationRes, reviewsRes] = await Promise.all([
        api.get('/bookings'),
        api.get('/notifications?unread_only=true'),
        api.get('/users/verification/status'),
        api.get('/users/reviews/pending').catch(() => ({ data: { pending_reviews: [] } }))
      ]);
      setBookings(bookingsRes.data.bookings || []);
      setNotifications(notificationsRes.data.notifications || []);
      setVerificationStatus(verificationRes.data);
      setPendingReviews(reviewsRes.data.pending_reviews || []);
      
      // Show review modal if there are pending reviews
      if (reviewsRes.data.pending_reviews?.length > 0) {
        setCurrentReviewBooking(reviewsRes.data.pending_reviews[0]);
        setShowReviewModal(true);
      }
      
      // P0 HOTFIX: If verification status was fixed on backend, refresh user context
      if (verificationRes.data.status_mismatch_fixed || verificationRes.data.status !== user?.verification_status) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const handleBecomeHost = async () => {
    try {
      const response = await api.post('/users/become-host');
      
      // P0 HOTFIX: Update user context with fresh data from response
      if (response.data.user) {
        updateUser(response.data.user);
      }
      
      toast.success(response.data.message || 'You are now a Host!');
      
      // Force refresh user context to ensure UI updates
      await refreshUser();
      
      // Navigate to Host Dashboard
      navigate('/host');
    } catch (error) {
      // Check if user needs to accept Host Service Agreement first
      if (error.response?.data?.detail?.includes('Host Service Agreement')) {
        navigate('/host-agreement');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to become host');
    }
  };

  // CANONICAL STATUS BADGE - Matches backend state machine
  const getStatusBadge = (status) => {
    const variants = {
      // Canonical statuses
      requested: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending Approval' },
      approved_pending_payment: { color: 'bg-blue-100 text-blue-800', icon: CreditCard, label: 'Awaiting Payment' },
      paid: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Paid' },
      prehire_in_progress: { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Pre-Hire In Progress' },
      prehire_submitted: { color: 'bg-indigo-100 text-indigo-800', icon: CheckCircle, label: 'Pre-Hire Complete' },
      active: { color: 'bg-emerald-100 text-emerald-800', icon: Car, label: 'Trip Active' },
      posthire_in_progress: { color: 'bg-orange-100 text-orange-800', icon: Clock, label: 'Return In Progress' },
      posthire_submitted: { color: 'bg-purple-100 text-purple-800', icon: Clock, label: 'Pending Review' },
      // P0 FIX: Add cooldown and finalisation statuses
      cooldown_window_open: { color: 'bg-indigo-100 text-indigo-800', icon: Clock, label: 'Post-Hire Review' },
      finalisation_pending: { color: 'bg-purple-100 text-purple-800', icon: Clock, label: 'Finalising' },
      completed: { color: 'bg-slate-100 text-slate-800', icon: CheckCircle, label: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Cancelled' },
      // P1 FIX: Payment expiry status
      approval_expired: { color: 'bg-slate-100 text-slate-600', icon: Clock, label: 'Expired' },
      // Legacy status mappings (backwards compat)
      accepted: { color: 'bg-blue-100 text-blue-800', icon: CreditCard, label: 'Awaiting Payment' },
      captured: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Confirmed' },
      declined: { color: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Declined' },
      returning: { color: 'bg-orange-100 text-orange-800', icon: Clock, label: 'Returning' }
    };
    const v = variants[status] || variants.requested;
    return (
      <Badge className={`${v.color} gap-1 text-xs`}>
        <v.icon className="w-3 h-3" />
        {v.label}
      </Badge>
    );
  };

  const getVerificationBadge = () => {
    // P0 FIX: If verification data is still loading, show loading state
    // This prevents showing stale verification status from cached user context
    if (loading || verificationStatus === null) {
      return <Badge className="bg-slate-100 text-slate-500 gap-1 text-xs animate-pulse"><Clock className="w-3 h-3" />Loading...</Badge>;
    }
    
    // P0 HOTFIX: Use fresh verification status from API, fallback to user context
    const status = verificationStatus?.status || user?.verification_status || 'unverified';
    if (status === 'verified') {
      return <Badge className="bg-green-100 text-green-800 gap-1 text-xs"><CheckCircle className="w-3 h-3" />Verified</Badge>;
    }
    if (status === 'pending') {
      return <Badge className="bg-yellow-100 text-yellow-800 gap-1 text-xs"><Clock className="w-3 h-3" />Pending</Badge>;
    }
    return <Badge className="bg-red-100 text-red-800 gap-1 text-xs"><AlertCircle className="w-3 h-3" />Unverified</Badge>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#004B8D] rounded-xl flex items-center justify-center">
              <Car className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="font-heading font-bold text-lg sm:text-xl text-[#004B8D] hidden xs:inline">HireHub</span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/vehicles">
              <Button variant="ghost" size="sm" className="gap-2 h-9 sm:h-10">
                <Search className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Search</span>
              </Button>
            </Link>
            <Link to="/messages" className="relative">
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" data-testid="messages-link">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <Link to="/notifications" className="relative">
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
                <Bell className="w-4 h-4 sm:w-5 sm:h-5" />
                {notifications.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#004B8D] rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <span className="hidden md:inline text-sm font-medium truncate max-w-[120px]">{user?.full_name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 lg:py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 mb-2">
            Welcome back, {user?.full_name?.split(' ')[0]}!
          </h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {getVerificationBadge()}
            {!loading && verificationStatus !== null && (verificationStatus?.status || user?.verification_status) !== 'verified' && (
              <Link to="/verification" className="text-xs sm:text-sm text-[#00A3BF] hover:underline">
                Complete verification to book vehicles
              </Link>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Link to="/vehicles">
            <Card className="cursor-pointer hover:shadow-md transition-all border-slate-100 h-full" data-testid="search-vehicles-card">
              <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#004B8D]/10 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                  <Search className="w-5 h-5 sm:w-6 sm:h-6 text-[#004B8D]" />
                </div>
                <span className="font-medium text-xs sm:text-sm">Search Vehicles</span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/verification">
            <Card className="cursor-pointer hover:shadow-md transition-all border-slate-100 h-full" data-testid="verification-card">
              <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00A3BF]/10 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-[#00A3BF]" />
                </div>
                <span className="font-medium text-xs sm:text-sm">Verification</span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/profile">
            <Card className="cursor-pointer hover:shadow-md transition-all border-slate-100 h-full" data-testid="profile-card">
              <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                  <User className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
                </div>
                <span className="font-medium text-xs sm:text-sm">Profile</span>
              </CardContent>
            </Card>
          </Link>

          <Link to="/dashboard/agreements">
            <Card className="cursor-pointer hover:shadow-md transition-all border-slate-100 h-full" data-testid="my-agreements-card">
              <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <span className="font-medium text-xs sm:text-sm">My Agreements</span>
              </CardContent>
            </Card>
          </Link>

          {/* P0 CRITICAL: Platform owner cannot become a host or see host options */}
          {!isPlatformOwner() && !isHost() ? (
            <Card 
              className={`cursor-pointer hover:shadow-md transition-all border-slate-100 h-full border-dashed ${
                (verificationStatus?.status || user?.verification_status) !== 'verified' ? 'opacity-50' : ''
              }`}
              onClick={() => {
                if ((verificationStatus?.status || user?.verification_status) !== 'verified') {
                  toast.error('Please complete identity verification first');
                  navigate('/verification');
                } else {
                  handleBecomeHost();
                }
              }}
              data-testid="become-host-card"
            >
              <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-50 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                  <Plus className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <span className="font-medium text-xs sm:text-sm">Become Host</span>
              </CardContent>
            </Card>
          ) : !isPlatformOwner() && isHost() ? (
            <Link to="/owner">
              <Card className="cursor-pointer hover:shadow-md transition-all border-slate-100 h-full" data-testid="host-dashboard-card">
                <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-50 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                    <Car className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                  <span className="font-medium text-xs sm:text-sm">Host Dashboard</span>
                </CardContent>
              </Card>
            </Link>
          ) : null}

          {/* Contact Support Card */}
          <Card 
            className="cursor-pointer hover:shadow-md transition-all border-slate-100 h-full" 
            onClick={async () => {
              try {
                const response = await api.post('/messages/threads', { thread_type: 'support' });
                navigate(`/messages/${response.data.thread_id}`);
              } catch (error) {
                toast.error('Failed to open support chat');
              }
            }}
            data-testid="contact-support-card"
          >
            <CardContent className="p-4 sm:p-6 flex flex-col items-center text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-50 rounded-xl flex items-center justify-center mb-2 sm:mb-3">
                <Headphones className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <span className="font-medium text-xs sm:text-sm">Contact Support</span>
            </CardContent>
          </Card>
        </div>

        {/* Admin Quick Access - Only show if user has admin roles */}
        {(isPlatformOwner?.() || isSuperAdmin?.() || isAdmin?.()) && (
          <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-purple-50 rounded-xl">
            <p className="text-xs sm:text-sm text-purple-800 font-medium mb-2">Admin Access</p>
            <div className="flex flex-wrap gap-2">
              {isPlatformOwner?.() && (
                <Link to="/platform-owner">
                  <Button variant="outline" size="sm" className="text-xs h-8">Platform Owner</Button>
                </Link>
              )}
              {isSuperAdmin?.() && (
                <Link to="/super-admin">
                  <Button variant="outline" size="sm" className="text-xs h-8">Super Admin</Button>
                </Link>
              )}
              {isAdmin?.() && (
                <Link to="/admin">
                  <Button variant="outline" size="sm" className="text-xs h-8">Admin Panel</Button>
                </Link>
              )}
            </div>
          </div>
        )}

        {/* My Bookings */}
        <Card className="border-slate-100">
          <CardHeader className="p-4 sm:p-6 pb-2 sm:pb-4">
            <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#004B8D]" />
              My Bookings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            {loading ? (
              <div className="flex items-center justify-center py-8 sm:py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <Car className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3 sm:mb-4" />
                <p className="text-slate-500 mb-3 sm:mb-4 text-sm sm:text-base">You haven't made any bookings yet</p>
                <Link to="/vehicles">
                  <Button className="bg-[#004B8D] hover:bg-[#003865] h-9 sm:h-10 text-sm">
                    Browse Vehicles
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bookings.slice(0, 5).map((booking) => (
                  <Link key={booking.booking_id} to={`/bookings/${booking.booking_id}`}>
                    <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                        {getVehiclePrimaryImage(booking.vehicle) ? (
                          <img 
                            src={getVehiclePrimaryImage(booking.vehicle)}
                            alt={`${booking.vehicle?.make} ${booking.vehicle?.model}`}
                            className="w-full h-full object-cover"
                            onError={(e) => handleVehicleImageError(e, booking.vehicle?.vehicle_id)}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Car className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">
                          {booking.vehicle?.make ? `${booking.vehicle.make} ${booking.vehicle.model}` : 'Vehicle'}
                        </p>
                        {booking.vehicle_missing && (
                          <p className="text-xs text-amber-600">Vehicle not found</p>
                        )}
                        <p className="text-xs sm:text-sm text-slate-500 truncate">
                          {booking.start_date} → {booking.end_date}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {getStatusBadge(booking.status)}
                        {/* P1 FIX: Show compact countdown for awaiting payment */}
                        {booking.status === 'approved_pending_payment' && booking.payment_deadline_info && (
                          <div className="mt-1">
                            <PaymentCountdown 
                              paymentDeadlineInfo={booking.payment_deadline_info}
                              compact={true}
                            />
                          </div>
                        )}
                        <p className="text-xs sm:text-sm text-slate-500 mt-1">
                          ${typeof booking.total_amount === 'number' ? booking.total_amount.toFixed(2) : booking.total_amount}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Settings */}
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <Link to="/profile">
            <Button variant="ghost" className="gap-2 h-9 sm:h-10 text-sm w-full sm:w-auto">
              <Settings className="w-4 h-4" />
              Settings
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 h-9 sm:h-10 text-sm w-full sm:w-auto" 
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
            Logout
          </Button>
        </div>

        {/* Credits & Referral Section */}
        <div className="mt-6 sm:mt-8 space-y-4">
          <CreditBalanceCard api={api} />
          <InviteAndEarn api={api} />
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
